import { useEffect } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSessionStorage } from 'usehooks-ts';
import {
  selectActiveNote,
  selectActiveVersion,
  selectVersionContentProcessing,
  setActiveNote,
  setShowNotePopoverOpened,
} from '../../../redux/store/content/slice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useGetApiNotesByVersionIdQuery } from '../../../redux/store/api/api';
import { MARK_VISIBILITY_GROUP_KEY } from '../../../shared/constants';
import { VisibilityGroupString } from '../../notes/types';

/**
 * Adds the listener responsible for opening the popover when a mark is clicked or activated via keyboard navigation.
 */
function useMakeMarksClickable() {
  const dispatch = useAppDispatch();
  const version = useAppSelector(selectActiveVersion);
  const activeNote = useAppSelector(selectActiveNote);
  const versionContentIsProcessing = useAppSelector(
    selectVersionContentProcessing,
  );
  const [activeMarkVisibilityGroup] =
    useSessionStorage<VisibilityGroupString | null>(
      MARK_VISIBILITY_GROUP_KEY,
      null,
    );

  const { data: noteData } = useGetApiNotesByVersionIdQuery(
    version.id
      ? {
          versionId: version.id,
        }
      : skipToken,
  );

  const findAndShowNote = (e: Event) => {
    const idExtentionRegex = /(-(\d)+\/(\d)+)$/g;
    const markEl =
      (e.target as HTMLElement).tagName === 'MARK'
        ? (e.target as HTMLElement)
        : (e.target as HTMLElement).children[0];
    const markId = markEl.id.replace(idExtentionRegex, '');
    const note =
      [
        ...(noteData?.resultObject?.userNotes || []),
        ...(noteData?.resultObject?.userGroupNotes || []),
        ...(noteData?.resultObject?.generalNotes || []),
      ].find((n) => n?.mark?.id === markId) || {};

    dispatch(setActiveNote(note));
    dispatch(setShowNotePopoverOpened(true));
  };

  useEffect(() => {
    // Make currently displayed marks clickable
    const marks = Array.from(document.getElementsByTagName('mark'));

    marks.forEach((mark) => {
      mark.parentElement?.addEventListener('click', (e) => findAndShowNote(e));
      mark.parentElement?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          findAndShowNote(e);
        }
      });
    });

    // Scroll to mark of recently added note if there is one
    if (activeNote.mark?.id) {
      const markEl = document.querySelector(`[id*="${activeNote.mark.id}"]`);
      if (markEl) {
        markEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        if ((activeNote.text?.length ?? 0) === 0) {
          dispatch(setActiveNote({}));
        }
      }
    }
  }, [versionContentIsProcessing, noteData, activeMarkVisibilityGroup]);
}

export default useMakeMarksClickable;
